// src/utils/filenameGenerator.js

/**
 * Utility for generating standardized report filenames
 * Format: reportname-company-date
 */

/**
 * Clean a string to be safe for filenames
 * @param {string} str - String to clean
 * @returns {string} Cleaned string
 */
const cleanForFilename = (str) => {
  if (!str || typeof str !== 'string') return '';
  
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 50); // Limit length
};

/**
 * Format date for filename (YYYY-MM-DD)
 * @param {Date} date - Date to format
 * @returns {string} Formatted date
 */
const formatDateForFilename = (date = new Date()) => {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

/**
 * Generate standardized filename for reports
 * @param {Object} options - Filename options
 * @param {string} options.reportType - Type of report (e.g., 'rapport-ebios-rm', 'atelier5')
 * @param {string} options.companyName - Company name
 * @param {string} options.analysisName - Analysis name
 * @param {string} options.extension - File extension (without dot)
 * @param {Date} options.date - Date for filename (defaults to current date)
 * @param {boolean} options.includeTime - Include time in filename (defaults to false)
 * @returns {string} Generated filename
 */
export const generateReportFilename = ({
  reportType = 'rapport-ebios-rm',
  companyName = '',
  analysisName = '',
  extension = 'html',
  date = new Date(),
  includeTime = false
}) => {
  const parts = [];
  
  // 1. Report type (always first)
  if (reportType) {
    parts.push(cleanForFilename(reportType));
  }
  
  // 2. Company name (if available)
  if (companyName) {
    parts.push(cleanForFilename(companyName));
  }
  
  // 3. Analysis name (if available and different from company)
  if (analysisName && analysisName !== companyName) {
    parts.push(cleanForFilename(analysisName));
  }
  
  // 4. Date
  let dateStr = formatDateForFilename(date);
  if (includeTime) {
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
    dateStr += `-${timeStr}`;
  }
  parts.push(dateStr);
  
  // Join parts and add extension
  const filename = parts.filter(part => part.length > 0).join('-');
  return `${filename}.${extension}`;
};

/**
 * Generate filename from report metadata
 * @param {Object} metadata - Report metadata
 * @param {string} reportType - Type of report
 * @param {string} extension - File extension
 * @param {Object} options - Additional options
 * @returns {string} Generated filename
 */
export const generateFilenameFromMetadata = (
  metadata = {}, 
  reportType = 'rapport-ebios-rm', 
  extension = 'html',
  options = {}
) => {
  return generateReportFilename({
    reportType,
    companyName: metadata.companyName || '',
    analysisName: metadata.analysisName || '',
    extension,
    includeTime: options.includeTime || false,
    date: options.date || new Date()
  });
};

/**
 * Generate filename for specific report types
 */
export const generateFilenames = {
  /**
   * Generate filename for complete EBIOS RM report
   */
  completeReport: (metadata, extension = 'html', options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-ebios-rm-complet', extension, options),
  
  /**
   * Generate filename for Workshop 5 report
   */
  workshop5Report: (metadata, extension = 'pdf', options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-atelier5', extension, options),
  
  /**
   * Generate filename for risk acceptance report
   */
  riskAcceptanceReport: (metadata, extension = 'pdf', options = {}) => 
    generateFilenameFromMetadata(metadata, 'acceptation-risques', extension, options),
  
  /**
   * Generate filename for text report
   */
  textReport: (metadata, options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-ebios-rm-texte', 'txt', options),
  
  /**
   * Generate filename for markdown report
   */
  markdownReport: (metadata, options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-ebios-rm-markdown', 'md', options),
  
  /**
   * Generate filename for HTML report
   */
  htmlReport: (metadata, options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-ebios-rm-html', 'html', options),
  
  /**
   * Generate filename for PDF report
   */
  pdfReport: (metadata, options = {}) => 
    generateFilenameFromMetadata(metadata, 'rapport-ebios-rm-pdf', 'pdf', options)
};

/**
 * Examples of generated filenames:
 * 
 * generateReportFilename({
 *   reportType: 'rapport-ebios-rm',
 *   companyName: 'Acme Corp',
 *   analysisName: 'Analyse S\u00e9curit\u00e9 2024',
 *   extension: 'html'
 * })
 * // Result: "rapport-ebios-rm-acme-corp-analyse-securite-2024-2024-01-15.html"
 * 
 * generateFilenames.workshop5Report({ 
 *   companyName: 'TechStart', 
 *   analysisName: 'Audit Cyber' 
 * })
 * // Result: "rapport-atelier5-techstart-audit-cyber-2024-01-15.pdf"
 * 
 * generateFilenames.riskAcceptanceReport({ 
 *   companyName: 'SecureCorp' 
 * }, 'html')
 * // Result: "acceptation-risques-securecorp-2024-01-15.html"
 */

export default {
  generateReportFilename,
  generateFilenameFromMetadata,
  generateFilenames,
  cleanForFilename,
  formatDateForFilename
};